import { useQuery } from '@tanstack/react-query';
import { useAgentSpecService } from '../../features/prompt-studio/services/agentSpecService';
import type { InvocationShapeType, RunnableAgent } from '../../types/backend';

// One cache entry per shape. Creating or retiring an agent invalidates `all()`, so every picker
// (rule editor, case dialog, the Studio's specialists) reads the change.
export const runnableAgentKeys = {
  all: () => ['agentspec', 'runnable-agents'] as const,
  shape: (shape: InvocationShapeType) => ['agentspec', 'runnable-agents', shape] as const,
};

/**
 * The active agents of one shape, as a picker offers them (agentspec.ListRunnableAgents).
 * Asks nothing until `enabled`; empty until the server answers, and on a failed read, which
 * `error` then names.
 */
export function useRunnableAgents(shape: InvocationShapeType, enabled: boolean) {
  const service = useAgentSpecService();
  const query = useQuery<RunnableAgent[]>({
    queryKey: runnableAgentKeys.shape(shape),
    queryFn: () => service.listRunnableAgents(shape),
    enabled,
  });
  return {
    agents: query.data ?? [],
    error: query.isError ? (query.error instanceof Error ? query.error.message : String(query.error)) : null,
  };
}
